import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { subcontractRequestsApi, type SubcontractRequest } from '@/lib/apiClient';
import { Button } from '@/components';
import { fmtDateTime } from '@/lib/dates';
import { jobHref } from './contractsModel';
import { requestHref, summarySections, requestStanding } from './subcontractRequestModel';
import { saveBlob, REVOKE_AFTER_MS } from './saveBlob';
import SubcontractRequestFiles from './SubcontractRequestFiles';

const dash = <span className="ct-none">—</span>;

// Moved to ./saveBlob so the documents section can share it; re-exported because the download's spec imports it from here.
export { REVOKE_AFTER_MS };

/**
 * One subcontract request (#627), read back in the order of the form.
 *
 * It is two screens in one, for the two people who open it:
 *   - the requester reviews it, and Submits (once it is complete), Edits, Withdraws or Discards;
 *   - the coordinator (`isCoordinator`) types the Intacct number onto it, and only then downloads the Word file.
 *
 * ⚠️ THE SERVER DECIDES BOTH WHAT IS MISSING AND WHICH BUTTONS WORK. `request.missing` and `request.access` are the
 * API's answers; a button is offered when `access` says it will work, and what a refusal says is shown as it is.
 * The Word file is made on the server on every download from what is saved, never stored: a corrected entry or a
 * changed number shows up in the next download.
 */
export default function SubcontractRequestDetail({ request, isCoordinator }: {
  request: SubcontractRequest;
  /** Holds contracts.edit: may type the number and download. The API checks it again either way. */
  isCoordinator: boolean;
}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const stored = request.subcontractNumber ?? '';
  const [numberText, setNumberText] = useState(stored);
  // The box follows the number the request holds. Without this, a number typed on it elsewhere arrives by a refetch and the
  // box still says what it opened with: it then reads as "changed", and Save would clear the number somebody else just typed.
  useEffect(() => { setNumberText(stored); }, [stored]);
  const a = request.access;
  const standing = requestStanding(request);

  const refreshed = (saved: SubcontractRequest) => {
    qc.setQueryData(['contracts', 'request', saved.id], saved);
    void qc.invalidateQueries({ queryKey: ['contracts'] });
    setError(null);
  };
  const fail = (e: unknown) => setError((e as Error)?.message || 'That did not work. Try again.');

  const submit = useMutation({ mutationFn: () => subcontractRequestsApi.submit(request.id), onSuccess: refreshed, onError: fail });
  const withdraw = useMutation({ mutationFn: () => subcontractRequestsApi.withdraw(request.id), onSuccess: refreshed, onError: fail });
  const discard = useMutation({
    mutationFn: () => subcontractRequestsApi.discard(request.id),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['contracts'] }); navigate(jobHref(request.projectId)); },
    onError: fail,
  });
  const setNumber = useMutation({
    mutationFn: () => subcontractRequestsApi.setNumber(request.id, numberText.trim() === '' ? null : numberText.trim()),
    onSuccess: refreshed,
    onError: fail,
  });
  const download = useMutation({
    mutationFn: () => subcontractRequestsApi.document(request.id),
    onSuccess: ({ blob, fileName }) => {
      setError(null);
      // The API names the file for its number, vendor and job. The name is only readable when the browser was allowed
      // to read the header, so keep a sensible one for the day it was not.
      saveBlob(blob, fileName ?? `Subcontract ${request.subcontractNumber ?? ''}`.trim() + '.docx');
      // The download records which template file it used on the request, so the next read shows it.
      void qc.invalidateQueries({ queryKey: ['contracts', 'request', request.id] });
    },
    onError: fail,
  });
  const busy = submit.isPending || withdraw.isPending || discard.isPending || setNumber.isPending || download.isPending;

  const typed = numberText.trim();
  const numberChanged = typed !== stored;
  const canDownload = isCoordinator && request.status === 'submitted' && request.numbered && !request.paperworkVoided && !numberChanged;
  const sections = summarySections(request);
  const jobLabel = `${request.jobNumber}${request.projectTitle ? ` · ${request.projectTitle}` : ''}`;

  return (
    <div className="ct-page">
      <Link className="ct-back" to={isCoordinator ? '/contracts' : jobHref(request.projectId)}>
        {isCoordinator ? '← Back to Contracts' : `← Back to job ${request.jobNumber}`}
      </Link>
      <div className="ct-page-head">
        <h1>Subcontract request{request.vendorName ? ` · ${request.vendorName}` : ''}</h1>
        <span className={`ct-chip ct-chip--${standing.tone}`}>{standing.label}</span>
      </div>
      <p className="ct-help">
        Job {jobLabel} · requested by {request.requestedBy.name ?? 'someone'}
        {request.submittedAt ? ` · submitted ${fmtDateTime(request.submittedAt)}` : ' · not submitted yet'}
      </p>

      {request.status === 'withdrawn' && (
        <div className="ct-hint" role="note">This request was withdrawn. Start a new one from the job’s Contracts tab if it is still needed.</div>
      )}
      {request.paperworkVoided && request.status === 'submitted' && (
        <div className="ct-hint" role="note">
          Its row on the Contracts page was voided, so no Word document is made for it.
        </div>
      )}
      {request.status !== 'withdrawn' && request.missing.length > 0 && (
        <div className="ct-missing" role="note">
          {request.status === 'draft' ? 'Before this can be submitted, it still needs:' : 'This request is incomplete, so its Word document can’t be made until it has:'}
          <ul>{request.missing.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}

      {error && <div className="ct-error" role="alert">{error}</div>}

      <div className="ct-actions">
        {a.canSubmit && (
          <Button
            variant="primary" onClick={() => submit.mutate()} isLoading={submit.isPending}
            disabled={busy || request.missing.length > 0}
            title={request.missing.length > 0 ? 'Fill in what is listed above first' : undefined}
          >
            Submit to the coordinator
          </Button>
        )}
        {a.canEdit && (
          <Link className="ew-btn-outline" to={`${requestHref(request.id)}/edit`}>Edit</Link>
        )}
        {a.canWithdraw && (
          <Button
            variant="secondary" disabled={busy}
            onClick={() => {
              if (window.confirm('Take this request back? Its row comes off the Contracts page.')) withdraw.mutate();
            }}
          >
            Withdraw
          </Button>
        )}
        {a.canDiscard && (
          <Button
            variant="danger" disabled={busy}
            onClick={() => {
              if (window.confirm('Discard this draft? It can’t be brought back.')) discard.mutate();
            }}
          >
            Discard draft
          </Button>
        )}
      </div>

      {isCoordinator && request.status === 'submitted' && (
        <section className="ct-rq-section" aria-labelledby="rq-number">
          <h2 id="rq-number">Intacct subcontract number</h2>
          <p className="ct-help">
            Create the subcontract in Intacct, then type its number here. The Word document is made once the request has a number,
            from what is saved now, every time you download it.
          </p>
          <div className="ct-number">
            <label className="ct-field">
              <span>Subcontract number</span>
              <input
                value={numberText} maxLength={30} placeholder="S00731" disabled={!a.canSetNumber}
                onChange={(e) => setNumberText(e.target.value)}
              />
            </label>
            <Button
              variant="primary" onClick={() => setNumber.mutate()} isLoading={setNumber.isPending}
              disabled={busy || !a.canSetNumber || !numberChanged}
            >
              Save number
            </Button>
            <Button
              variant="secondary" onClick={() => download.mutate()} isLoading={download.isPending}
              disabled={busy || !canDownload}
              title={canDownload ? undefined : numberChanged ? 'Save the number first' : 'The document is made once the request has a number'}
            >
              Download Word
            </Button>
          </div>
          {!a.canSetNumber && (
            <p className="ct-help">
              {request.paperworkRemoved
                ? 'Its row was taken off the Contracts page, so the number can’t be changed here.'
                : 'The number can’t be changed on this request.'}
            </p>
          )}
          {request.templateVersion && (
            <p className="ct-help">The last document was made from template file {request.templateVersion}.</p>
          )}
        </section>
      )}

      {/* The documents sit above the summary, where a draft is reviewed and a coordinator looks first. `canEdit` is the request's own
          answer, so a coordinator on a numbered request can still add one and a requester on a processed one can only download. */}
      <SubcontractRequestFiles key={request.id} requestId={request.id} canEdit={a.canEdit} />

      {sections.map((s) => (
        <section key={s.title} className="ct-rq-section" aria-label={s.title}>
          <h2>{s.title}</h2>
          <dl className="ct-summary">
            {s.rows.map((r) => (
              <div key={r.label} className="ct-summary-row">
                <dt>{r.label}</dt>
                <dd className={r.block ? 'ct-block' : undefined}>{r.value === '' ? dash : r.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      ))}
    </div>
  );
}
